import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Avatar, AvatarFallback, AvatarImage } from '@org/ui';
import { LikersModal } from './likers-modal';

interface LikersStackProps {
  postId: string;
  likesCount: number;
}

const MAX_AVATARS = 3;

export function LikersStack({ postId, likesCount }: LikersStackProps) {
  const { t } = useTranslation('community');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: likers } = communityTrpc.likes.listForPost.useQuery(
    { postId, limit: MAX_AVATARS },
    { enabled: likesCount > 0 }
  );

  if (likesCount === 0) return null;

  const displayLikers = likers ?? [];
  const remaining = likesCount - displayLikers.length;

  return (
    <>
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setModalOpen(true);
        }}
        aria-label={t('likers.viewAll')}
      >
        <div className="flex -space-x-2">
          {displayLikers.map((liker) => {
            const initials =
              (liker.firstName?.[0] ?? '') + (liker.lastName?.[0] ?? '');
            return (
              <Avatar
                key={liker.userId}
                className="h-6 w-6 border-2 border-background"
              >
                <AvatarImage
                  src={liker.profileImageUrl ?? undefined}
                  alt={
                    liker.displayName ||
                    `${liker.firstName} ${liker.lastName}`.trim()
                  }
                />
                <AvatarFallback className="bg-primary/10 text-[10px] text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
            );
          })}
        </div>

        {remaining > 0 && (
          <span>
            {t('likers.andOthers', { count: remaining })}
          </span>
        )}
      </button>

      <LikersModal
        postId={postId}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
